import { isLiabilityAccountType } from '@fruitful/fruitils';
import {
    AccountCategory,
    type AccountSummary,
    Provider,
} from '@fruitful/types';
import { router } from 'expo-router';
import { useSetAtom } from 'jotai';
import React, { useState } from 'react';
import {
    BadgeLabel,
    BodyMediumRegular,
    Button,
    ButtonVariant,
    CircleAlertIcon,
    EditIcon,
    Icon,
    InfoIcon,
    InstitutionLogo,
    isEmpty,
    LabelRegular,
    MoreHorizontalIcon,
    Separator,
    View,
    XStack,
    YStack,
} from 'smoothie';

import FailedConnectionSheet from '../../onboarding/components/FailedConnectionSheet';
import { RemoveBankSheet } from '../../onboarding/components/RemoveBankSheet';
import {
    selectedManualAccountAtom,
    selectedManualInstitutionAtom,
} from '../../onboarding/state/OnboardingAtoms';
import useReconnectQuilttInstitution from '../hooks/useReconnectQuilttInstitution';
import { selectedHolisticAccountAtom } from '../state/HolisticAtoms';
import type { QuilttConnectorSource } from '../types/Quiltt';

interface InstitutionAccountProps {
    institutionName: string;
    institutionLogo: string;
    connectionId: string;
    accounts: AccountSummary[];
    isManual?: boolean;
    context: QuilttConnectorSource;
    shouldRenderAddAccountButton?: boolean;
}

const InstitutionAccounts = ({
    institutionName,
    institutionLogo,
    connectionId,
    accounts,
    isManual,
    context,
    shouldRenderAddAccountButton = true,
}: InstitutionAccountProps) => {
    const setSelectedHolisticAccount = useSetAtom(selectedHolisticAccountAtom);
    const setSelectedManualAccount = useSetAtom(selectedManualAccountAtom);
    const setSelectedManualInstitution = useSetAtom(
        selectedManualInstitutionAtom,
    );

    const [isRemoveSheetOpen, setIsRemoveSheetOpen] = useState(false);
    const [showFailedConnectionSheet, setShowFailedConnectionSheet] =
        useState(false);

    const { reconnectInstitution } = useReconnectQuilttInstitution(
        connectionId,
        context,
    );

    const canEdit = context !== 'onboarding';

    return (
        <YStack
            borderWidth={1}
            borderRadius={16}
            borderColor="$gray400"
            overflow="hidden"
        >
            <XStack
                backgroundColor="$gray200"
                paddingHorizontal={16}
                paddingVertical={12}
                alignItems="center"
                justifyContent="space-between"
            >
                <XStack gap={12} alignItems="center" flexShrink={1}>
                    {!isEmpty(institutionLogo) && (
                        <InstitutionLogo logo={institutionLogo} size="xs" />
                    )}
                    <BodyMediumRegular
                        numberOfLines={1}
                        ellipsizeMode="tail"
                        style={{ flexShrink: 1 }}
                    >
                        {institutionName}
                    </BodyMediumRegular>
                    {accounts.length > 0 && (
                        <LabelRegular
                            color="$gray1000"
                            numberOfLines={1}
                        >{`${accounts.length} account${accounts.length > 1 ? 's' : ''}`}</LabelRegular>
                    )}
                </XStack>
                {canEdit && (
                    <View
                        cursor="pointer"
                        onPress={() => {
                            setIsRemoveSheetOpen(true);
                        }}
                    >
                        <Icon icon={MoreHorizontalIcon} size={20} />
                    </View>
                )}
            </XStack>
            <YStack paddingHorizontal={16} separator={<Separator />}>
                {accounts.length === 0 ? (
                    <YStack paddingVertical={16} gap={12}>
                        <XStack
                            gap={8}
                            alignItems="center"
                            backgroundColor="$orange300"
                            padding={12}
                            borderRadius={16}
                        >
                            <Icon
                                color="$orange1100"
                                icon={CircleAlertIcon}
                                size={20}
                            />
                            <BodyMediumRegular color="$orange1100">
                                Something went wrong with this connection.
                            </BodyMediumRegular>
                        </XStack>
                        <Button
                            text="Try again"
                            variant={ButtonVariant.SECONDARY}
                            onPress={() => {
                                setShowFailedConnectionSheet(true);
                            }}
                        />
                    </YStack>
                ) : (
                    accounts.map((account) => {
                        const isLiability = isLiabilityAccountType(
                            account.type,
                        );
                        const isCredit =
                            account.type?.toLowerCase() === 'credit';

                        const needsDetails =
                            (isCredit &&
                                account.entireBalancePaidMonthly == null) ||
                            (isLiability &&
                                account.category.toLowerCase() !==
                                    AccountCategory.SPEND.toLowerCase() &&
                                (account.minimumPayment == null ||
                                    account.interestRate == null));

                        return (
                            <XStack
                                cursor="pointer"
                                paddingVertical={16}
                                gap={16}
                                key={account.id}
                                alignItems="center"
                                justifyContent="space-between"
                                onPress={() => {
                                    if (canEdit) {
                                        if (
                                            account.provider === Provider.MANUAL
                                        ) {
                                            setSelectedManualInstitution(
                                                institutionName,
                                            );
                                            setSelectedManualAccount(account);
                                            router.navigate(
                                                '/holistic/edit-manual-account',
                                            );
                                        } else {
                                            setSelectedHolisticAccount(account);
                                            router.navigate(
                                                '/holistic/edit-institution-account',
                                            );
                                        }
                                    }
                                }}
                            >
                                <XStack flex={1} alignItems="center" gap={16}>
                                    <BodyMediumRegular
                                        numberOfLines={1}
                                        ellipsizeMode="tail"
                                        style={{ flexShrink: 1 }}
                                    >
                                        {account.name}{' '}
                                        {account.provider !== Provider.MANUAL &&
                                            `*${account.accountNumberMasked}`}
                                    </BodyMediumRegular>
                                </XStack>
                                {canEdit && (
                                    <BadgeLabel
                                        variant={
                                            needsDetails ? 'warning' : 'neutral'
                                        }
                                        text={
                                            needsDetails
                                                ? 'Add details'
                                                : 'Edit'
                                        }
                                        icon={
                                            needsDetails ? InfoIcon : EditIcon
                                        }
                                        size="regular"
                                    />
                                )}
                            </XStack>
                        );
                    })
                )}
                {shouldRenderAddAccountButton && !!isManual && (
                    <XStack
                        cursor="pointer"
                        paddingVertical={16}
                        alignItems="center"
                        justifyContent="space-between"
                        onPress={() => {
                            setSelectedManualAccount(null);
                            setSelectedManualInstitution(institutionName);
                            router.navigate('/holistic/edit-manual-account');
                        }}
                    >
                        <BodyMediumRegular>+ Add account</BodyMediumRegular>
                    </XStack>
                )}
            </YStack>
            <RemoveBankSheet
                isOpen={isRemoveSheetOpen}
                connectionId={connectionId}
                institutionName={institutionName}
                onClose={() => {
                    setIsRemoveSheetOpen(false);
                }}
            />
            {showFailedConnectionSheet && (
                <FailedConnectionSheet
                    onDismiss={() => {
                        setShowFailedConnectionSheet(false);
                    }}
                    institutionName={institutionName}
                    connectInstitution={reconnectInstitution}
                />
            )}
        </YStack>
    );
};

export default InstitutionAccounts;
